import type {
  GrammarExample,
  GrammarScriptText
} from '@/content/grammar/schema'
import type { ReaderBlock, ReaderContent } from '@/types/reader'
import type { StudyNoteInput } from '@/types/study'
import { upsertLearningContentState } from './learning-state'
import {
  grammarLearningLocator,
  wordLearningLocator
} from './learning-state-core'
import { ensureDefaultStudyDeck, saveStudyNoteToDeck } from './study'

export type StudyWordEntry = {
  id: number
  simplified: string
  traditional?: string | null
  pinyin?: string | null
  zhuyin?: string | null
  definitions?: string[]
}

export function scriptTextValue(text: string | GrammarScriptText): string {
  return typeof text === 'string' ? text : text.s
}

function currentHref() {
  if (typeof window === 'undefined') return null
  return `${window.location.pathname}${window.location.search}`
}

export function buildDictionaryWordStudyNoteInput(
  entry: StudyWordEntry,
  source: Record<string, unknown> = {}
): StudyNoteInput {
  return {
    noteType: 'word',
    dictEntryId: entry.id,
    fields: {
      simplified: entry.simplified,
      traditional: entry.traditional || entry.simplified,
      pinyin: entry.pinyin || '',
      zhuyin: entry.zhuyin || '',
      definitions: entry.definitions ?? []
    },
    source: {
      sourceType: 'dictionary',
      source_type: 'dictionary',
      dictEntryId: entry.id,
      dict_entry_id: entry.id,
      href: currentHref(),
      ...source
    },
    tags: ['dictionary']
  }
}

export function buildReaderSentenceStudyNoteInput({
  content,
  block,
  sentenceText,
  readerHref = currentHref()
}: {
  content: ReaderContent
  block: ReaderBlock
  sentenceText: string
  readerHref?: string | null
}): StudyNoteInput {
  const grammarConceptIds = Array.from(
    new Set((block.grammarUsages ?? []).map((usage) => usage.conceptId))
  )
  const storySlug =
    content.sourceType === 'story' ? content.metadata?.slug : null

  return {
    noteType: 'sentence',
    fields: {
      text: sentenceText,
      translation: block.translation ?? null,
      source_title: content.title
    },
    source: {
      sourceType: content.sourceType,
      source_type: content.sourceType,
      contentId: content.id,
      content_id: content.id,
      blockId: block.id,
      block_id: block.id,
      sentenceId: block.sourceSentenceId ?? block.id,
      sentence_id: block.sourceSentenceId ?? block.id,
      storySlug,
      story_slug: storySlug,
      storyTitle: content.sourceType === 'story' ? content.title : null,
      story_title: content.sourceType === 'story' ? content.title : null,
      title: content.title,
      readerHref,
      reader_href: readerHref,
      grammarConceptIds,
      grammar_concept_ids: grammarConceptIds
    },
    tags: Array.from(
      new Set([
        'reader',
        content.sourceType,
        ...(storySlug ? [`story:${storySlug}`] : []),
        ...grammarConceptIds.map((id) => `grammar:${id}`)
      ])
    )
  }
}

export function buildGrammarExampleStudyNoteInput({
  conceptId,
  grammarTitle,
  example,
  grammarHref = currentHref()
}: {
  conceptId: string
  grammarTitle: string
  example: GrammarExample
  grammarHref?: string | null
}): StudyNoteInput {
  return {
    noteType: 'sentence',
    fields: {
      text: scriptTextValue(example.zh),
      translation: example.translation,
      pinyin: example.pinyin,
      source_title: grammarTitle
    },
    source: {
      sourceType: 'grammar',
      source_type: 'grammar',
      conceptId,
      concept_id: conceptId,
      grammarTitle,
      grammar_title: grammarTitle,
      exampleId: example.id,
      example_id: example.id,
      grammarHref,
      grammar_href: grammarHref
    },
    tags: ['grammar', `grammar:${conceptId}`]
  }
}

export async function saveDictionaryWordToDefaultDeck(
  userId: string,
  entry: StudyWordEntry,
  source?: Record<string, unknown>
) {
  const deck = await ensureDefaultStudyDeck('saved_words')
  const saved = await saveStudyNoteToDeck(
    userId,
    deck.id,
    buildDictionaryWordStudyNoteInput(entry, source)
  )
  await upsertLearningContentState({
    userId,
    ...wordLearningLocator(entry.id),
    status: 'saved',
    metadata: {
      simplified: entry.simplified,
      traditional: entry.traditional || entry.simplified,
      sourceType: source?.sourceType ?? source?.source_type ?? 'dictionary'
    }
  })
  return saved
}

export async function saveReaderSentenceToDefaultDeck(
  userId: string,
  input: Parameters<typeof buildReaderSentenceStudyNoteInput>[0]
) {
  const deck = await ensureDefaultStudyDeck('sentence_mining')
  return saveStudyNoteToDeck(
    userId,
    deck.id,
    buildReaderSentenceStudyNoteInput(input)
  )
}

export async function saveGrammarExampleToDefaultDeck(
  userId: string,
  input: Parameters<typeof buildGrammarExampleStudyNoteInput>[0]
) {
  const deck = await ensureDefaultStudyDeck('sentence_mining')
  const saved = await saveStudyNoteToDeck(
    userId,
    deck.id,
    buildGrammarExampleStudyNoteInput(input)
  )
  await upsertLearningContentState({
    userId,
    ...grammarLearningLocator(input.conceptId),
    status: 'studying',
    metadata: {
      grammarTitle: input.grammarTitle,
      sourceType: 'grammar'
    }
  })
  return saved
}
